import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  listGroups, createGroup, renameGroup, deleteGroup,
  addGroupMember, removeGroupMember, searchUsers,
} from '../../services/api';
import { getCurrentUserId } from '../../services/auth';
import styles from './GroupManager.module.css';

export default function GroupManager() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);

  // per-group state: keyed by group id
  const [groupState, setGroupState] = useState({});

  const currentUserId = getCurrentUserId();
  const debounceRef = useRef({});
  const addWrapperRefs = useRef({});

  const loadGroups = useCallback(async () => {
    try {
      const data = await listGroups();
      setGroups(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const setGS = (groupId, patch) =>
    setGroupState(prev => ({ ...prev, [groupId]: { ...prev[groupId], ...patch } }));

  const gs = (groupId) => groupState[groupId] || {};

  // ── create group ────────────────────────────────────────────────────────

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    setError(null);
    try {
      const group = await createGroup(newGroupName.trim());
      setGroups(prev => [...prev, group]);
      setNewGroupName('');
      setShowNewGroupInput(false);
      setExpandedId(group.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreatingGroup(false);
    }
  };

  // ── rename group ────────────────────────────────────────────────────────

  const handleRename = async (groupId) => {
    const { renameValue } = gs(groupId);
    if (!renameValue?.trim()) return;
    setGS(groupId, { renaming: true, renameError: null });
    try {
      const updated = await renameGroup(groupId, renameValue.trim());
      setGroups(prev => prev.map(g => g.id === groupId ? updated : g));
      setGS(groupId, { renaming: false, editingName: false });
    } catch (e) {
      setGS(groupId, { renaming: false, renameError: e.message });
    }
  };

  // ── delete group ────────────────────────────────────────────────────────

  const handleDelete = async (groupId) => {
    setGS(groupId, { deleting: true });
    try {
      await deleteGroup(groupId);
      setGroups(prev => prev.filter(g => g.id !== groupId));
      if (expandedId === groupId) setExpandedId(null);
    } catch (e) {
      setGS(groupId, { deleting: false, deleteError: e.message });
    }
  };

  // ── add member ──────────────────────────────────────────────────────────

  const handleAddEmailChange = (groupId, value, existingMembers) => {
    setGS(groupId, { addEmail: value, addError: null, addDropdownOpen: true });
    clearTimeout(debounceRef.current[groupId]);
    if (value.length < 2) {
      setGS(groupId, { addSuggestions: [] });
      return;
    }
    debounceRef.current[groupId] = setTimeout(async () => {
      try {
        const results = await searchUsers(value);
        const memberIds = new Set(existingMembers.map(m => m.user_id));
        setGS(groupId, { addSuggestions: results.filter(u => !memberIds.has(u.id)) });
      } catch {
        setGS(groupId, { addSuggestions: [] });
      }
    }, 200);
  };

  const handleAddMember = async (groupId, emailOverride) => {
    const email = emailOverride ?? gs(groupId).addEmail;
    if (!email?.trim()) return;
    setGS(groupId, { addingMember: true, addError: null, addSuggestions: [], addDropdownOpen: false });
    try {
      const updated = await addGroupMember(groupId, email.trim());
      setGroups(prev => prev.map(g => g.id === groupId ? updated : g));
      setGS(groupId, { addingMember: false, addEmail: '' });
    } catch (e) {
      setGS(groupId, { addingMember: false, addError: e.message });
    }
  };

  // ── remove member ───────────────────────────────────────────────────────

  const handleRemoveMember = async (groupId, userId) => {
    setGS(groupId, { removingMemberId: userId });
    try {
      const updated = await removeGroupMember(groupId, userId);
      setGroups(prev => prev.map(g => g.id === groupId ? updated : g));
    } catch (e) {
      setGS(groupId, { removingMemberId: null, removeMemberError: e.message });
    } finally {
      setGS(groupId, { removingMemberId: null });
    }
  };

  if (loading) return <div className={styles.empty}>Loading groups…</div>;

  return (
    <div className={styles.root}>
      {error && <div className={styles.errorBanner}>{error}</div>}

      {groups.length === 0 && !showNewGroupInput && (
        <div className={styles.empty}>No groups yet.</div>
      )}

      {groups.map(group => {
        const state = gs(group.id);
        const isExpanded = expandedId === group.id;
        const isCreator = group.created_by === currentUserId;

        return (
          <div key={group.id} className={styles.groupCard}>
            <div
              className={styles.groupHeader}
              onClick={() => setExpandedId(isExpanded ? null : group.id)}
            >
              <span className={styles.groupChevron}>{isExpanded ? '▾' : '▸'}</span>
              {state.editingName ? (
                <input
                  className={styles.renameInput}
                  value={state.renameValue ?? group.name}
                  autoFocus
                  onClick={e => e.stopPropagation()}
                  onChange={e => setGS(group.id, { renameValue: e.target.value })}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleRename(group.id);
                    if (e.key === 'Escape') setGS(group.id, { editingName: false });
                  }}
                />
              ) : (
                <span className={styles.groupName}>{group.name}</span>
              )}
              <span className={styles.groupMeta}>{group.members.length} member{group.members.length !== 1 ? 's' : ''}</span>
              <div className={styles.groupActions} onClick={e => e.stopPropagation()}>
                {state.editingName ? (
                  <>
                    <button
                      className={styles.actionBtn}
                      disabled={state.renaming}
                      onClick={() => handleRename(group.id)}
                    >
                      {state.renaming ? '…' : 'Save'}
                    </button>
                    <button
                      className={styles.actionBtnMuted}
                      onClick={() => setGS(group.id, { editingName: false })}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className={styles.actionBtnMuted}
                      title="Rename group"
                      onClick={() => setGS(group.id, { editingName: true, renameValue: group.name })}
                    >
                      Rename
                    </button>
                    {isCreator && (
                      <button
                        className={styles.actionBtnDanger}
                        title="Delete group"
                        disabled={state.deleting}
                        onClick={() => {
                          if (window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) {
                            handleDelete(group.id);
                          }
                        }}
                      >
                        {state.deleting ? '…' : 'Delete'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {state.renameError && (
              <div className={styles.inlineError}>{state.renameError}</div>
            )}
            {state.deleteError && (
              <div className={styles.inlineError}>{state.deleteError}</div>
            )}

            {isExpanded && (
              <div className={styles.groupBody}>
                <div className={styles.memberList}>
                  {group.members.map(m => (
                    <div key={m.user_id} className={styles.memberRow}>
                      <span className={styles.memberName}>
                        {m.display_name || m.email}
                        {m.user_id === group.created_by && (
                          <span className={styles.creatorBadge}>creator</span>
                        )}
                      </span>
                      <span className={styles.memberEmail}>{m.display_name ? m.email : ''}</span>
                      <button
                        className={styles.removeMemberBtn}
                        disabled={state.removingMemberId === m.user_id}
                        title="Remove from group"
                        onClick={() => handleRemoveMember(group.id, m.user_id)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                {state.removeMemberError && (
                  <div className={styles.inlineError}>{state.removeMemberError}</div>
                )}

                <div
                  className={styles.addMemberWrapper}
                  ref={el => { addWrapperRefs.current[group.id] = el; }}
                >
                  <div className={styles.addMemberRow}>
                    <input
                      className={styles.addMemberInput}
                      type="text"
                      placeholder="Add member by email…"
                      value={state.addEmail || ''}
                      autoComplete="off"
                      onChange={e => handleAddEmailChange(group.id, e.target.value, group.members)}
                      onFocus={() => setGS(group.id, { addDropdownOpen: true })}
                      onBlur={() => setTimeout(() => setGS(group.id, { addDropdownOpen: false }), 150)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleAddMember(group.id);
                        if (e.key === 'Escape') setGS(group.id, { addSuggestions: [], addDropdownOpen: false });
                      }}
                    />
                    <button
                      className={styles.addMemberBtn}
                      disabled={state.addingMember || !state.addEmail?.trim()}
                      onClick={() => handleAddMember(group.id)}
                    >
                      {state.addingMember ? '…' : 'Add'}
                    </button>
                  </div>
                  {state.addDropdownOpen && state.addSuggestions?.length > 0 && (() => {
                    const rect = addWrapperRefs.current[group.id]?.getBoundingClientRect();
                    if (!rect) return null;
                    return createPortal(
                      <ul
                        className={styles.addDropdown}
                        style={{ top: rect.bottom + 4, left: rect.left, width: rect.width }}
                      >
                        {state.addSuggestions.map(u => (
                          <li
                            key={u.id}
                            className={styles.addDropdownItem}
                            onMouseDown={() => handleAddMember(group.id, u.email)}
                          >
                            <span className={styles.addDropdownName}>{u.display_name || u.email}</span>
                            {u.display_name && <span className={styles.addDropdownEmail}>{u.email}</span>}
                          </li>
                        ))}
                      </ul>,
                      document.body
                    );
                  })()}
                </div>
                {state.addError && (
                  <div className={styles.inlineError}>{state.addError}</div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {showNewGroupInput ? (
        <div className={styles.newGroupRow}>
          <input
            className={styles.newGroupInput}
            type="text"
            placeholder="Group name…"
            value={newGroupName}
            autoFocus
            onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleCreateGroup();
              if (e.key === 'Escape') { setShowNewGroupInput(false); setNewGroupName(''); }
            }}
          />
          <button
            className={styles.createBtn}
            disabled={creatingGroup || !newGroupName.trim()}
            onClick={handleCreateGroup}
          >
            {creatingGroup ? '…' : 'Create'}
          </button>
          <button
            className={styles.cancelNewBtn}
            onClick={() => { setShowNewGroupInput(false); setNewGroupName(''); }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          className={styles.newGroupBtn}
          onClick={() => setShowNewGroupInput(true)}
        >
          + New Group
        </button>
      )}
    </div>
  );
}
